import { useMemo } from 'react';
import { Link } from 'react-router';
import { ReactComponent as ChevronLeft } from '@rocco/icons/svg/arrow/arrow-chevron-left.svg';

interface BackButtonProps {
    parentCategoryName: string;
    parentCategoryLink: string;
    onClick?: () => void;
}

/**
 * Back button component for Products page filter bar navigation.
 * Navigates to the parent category in the product hierarchy.
 *
 * @example
 * ```tsx
 * <BackButton
 *   parentCategoryName="Ceiling Lighting"
 *   parentCategoryLink="/products/lighting/ceiling-lighting"
 * />
 * ```
 *
 * @param parentCategoryName - Display name of parent category
 * @param parentCategoryLink - URL path to parent category
 */
export const BackButton = ({
    parentCategoryName,
    parentCategoryLink,
    onClick,
}: BackButtonProps) => {
    // Validate URL to prevent XSS - only allow same-origin paths with safe protocols
    const safeLink = useMemo(() => {
        // SSR-safe check: during server-side rendering, only allow relative paths
        if (typeof window === 'undefined') {
            if (parentCategoryLink.startsWith('/')) {
                return parentCategoryLink;
            }
            return '/products';
        }

        try {
            // Parse URL to validate protocol and origin
            const url = new URL(parentCategoryLink, window.location.origin);

            // Only allow http and https protocols
            const allowedProtocols = ['http:', 'https:'];
            if (!allowedProtocols.includes(url.protocol)) {
                console.error('Unsafe protocol blocked');
                return '/products';
            }

            // Block cross-origin URLs
            if (url.origin !== window.location.origin) {
                console.error('Cross-origin navigation blocked');
                return '/products';
            }

            return url.pathname + url.search;
        } catch (error) {
            console.error('Invalid URL:', error);
            return '/products';
        }
    }, [parentCategoryLink]);

    const sharedClassName =
        'flex h-10.5 shrink-0 items-center gap-1 rounded-full border border-black pl-3.5 pr-5 transition-colors hover:bg-black/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 active:bg-black/10';

    const content = (
        <>
            <ChevronLeft
                aria-hidden="true"
                className="size-3.5 text-[#141414]"
            />
            <span className="line-clamp-1 max-w-50 text-base tracking-tighter text-black">
                {parentCategoryName}
            </span>
        </>
    );

    if (onClick) {
        return (
            <button
                type="button"
                onClick={onClick}
                aria-label={`Back to ${parentCategoryName}`}
                className={sharedClassName}
            >
                {content}
            </button>
        );
    }

    return (
        <Link
            to={safeLink}
            state={{ restoreScroll: true, scroll: false }}
            aria-label={`Back to ${parentCategoryName}`}
            className={sharedClassName}
        >
            {content}
        </Link>
    );
};
